// src/pages/CompleteRegistration.js (v2 – config-driven, clean)

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import ResendRegistrationLinkBanner from "../components/ResendRegistrationLinkBanner";
import "../styles/CompleteRegistration.css";

/**
 * Page for completing registration with a token from email.
 *
 * Sets password, validates token, logs in, and routes by role.
 *
 * @component
 * @returns {JSX.Element}
 */
const CompleteRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);

  // Extract token from URL and pre-fill email if possible
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token") || "";
    setToken(t);

    if (t) {
      try {
        const decoded = jwtDecode(t);
        if (decoded?.email) setEmail(decoded.email);
      } catch {
        // Invalid/expired token — backend will handle real validation
      }
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowResend(false);

    if (!token) {
      setError("Missing registration token.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post(API_PATHS.COMPLETE_REGISTRATION, {
        token,
        new_password: password,
      });

      const access = data?.access_token;
      const refresh = data?.refresh_token;

      if (!access) {
        throw new Error("Registration succeeded but no token returned.");
      }

      localStorage.setItem("token", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);

      // Route by role
      const decoded = jwtDecode(access);
      const role = decoded?.role?.toUpperCase();

      if (role === "ADMIN") navigate(ROUTES.ADMIN, { replace: true });
      else if (role === "PRINCIPAL") navigate(ROUTES.PRINCIPAL_DASHBOARD, { replace: true });
      else if (role === "STAFF") navigate(ROUTES.STAFF_DASHBOARD, { replace: true });
      else navigate(ROUTES.VIEW_PHOTOS, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(detail);

      if (String(detail).toLowerCase().includes("expired")) {
        setShowResend(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="complete-registration-page">
      <div className="complete-registration-container">
        <h2 className="form-title">Complete your GhrianGhraffy setup</h2>
        <p className="complete-registration-subtitle">
          Choose a secure password to finish activating your account.
        </p>

        {error && (
          <p className="error-message" aria-live="polite">
            {error}
          </p>
        )}

        {showResend && email && (
          <div className="resend-banner-wrapper">
            <ResendRegistrationLinkBanner email={email} />
          </div>
        )}

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-group">
            <label className="form-label">Your email</label>
            <input
              type="email"
              className="form-input form-input--readonly"
              value={email || ""}
              readOnly
            />
          </div>

          <div className="form-group">
            <label className="form-label">Create a new password</label>
            <input
              type="password"
              className="form-input green-placeholder"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <small className="field-hint">
              Tip: avoid re-using your email password. Use a mix of words, numbers, and symbols.
            </small>
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Completing…" : "Login for First time"}
          </button>
        </form>

        <p className="complete-registration-footer">
          Already set up? <Link to={ROUTES.LOGIN}>Go to Login</Link>
        </p>
      </div>
    </div>
  );
};

export default CompleteRegistration;